import React from "react";
import styled from "styled-components";
import { motion, useScroll, useTransform } from "framer-motion";
import img1 from "../../assets/homeAssets/aboutMe1.JPG";
import img2 from "../../assets/homeAssets/aboutMe2.JPG";
import { Link } from "react-router-dom";

// --- Styled Components ---

const AboutSection = styled.section`
  padding: 150px 20px;
  background-color: #f4efed;
  display: flex;
  justify-content: center;
  overflow: hidden;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  width: 100%;
  max-width: 1200px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ImageComposition = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MainImage = styled(motion.div)`
  width: 80%;
  height: 550px;
  border-radius: 200px 200px 0 0;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SecondaryImage = styled(motion.div)`
  position: absolute;
  bottom: -40px;
  right: 0;
  width: 220px;
  height: 280px;
  border-radius: 20px;
  overflow: hidden;
  border: 10px solid #f4efed;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TextSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const Subtitle = styled(motion.span)`
  font-family: "Georgia", serif;
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.85rem;
  color: #a38d92;
`;

const Title = styled(motion.h2)`
  font-family: "Lovelace", serif;
  font-style: italic;
  font-size: clamp(3rem, 5vw, 4.5rem);
  font-weight: 900;
  line-height: 1.1;
  color: #5c4c4c;
  margin: 0;
`;

const BodyText = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #4a3a3d;
  max-width: 450px;
`;

const Signature = styled(motion.div)`
  font-family: "Brush Script MT", cursive;
  font-size: 3rem;
  color: #8e7479;
  margin-top: 10px;
  opacity: 0.8;
`;

const MoreButton = styled(motion.button)`
  align-self: flex-start;
  margin-top: 20px;
  padding: 18px 40px;
  background-color: transparent;
  border: 1px solid #1a1a1a;
  color: #1a1a1a;
  font-family: "Georgia", serif;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  border-radius: 50px;
  transition: all 0.4s ease;

  &:hover {
    background-color: #b89696;
    color: #fff;
  }
`;

// --- Component ---

const AboutMe = () => {
  const { scrollYProgress } = useScroll();

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <AboutSection>
      <Wrapper>
        <ImageComposition>
          <MainImage style={{ y: y1 }}>
            <img src={img1} alt="Simi Portrait" />
          </MainImage>
          <SecondaryImage style={{ y: y2 }}>
            <img src={img2} alt="Lash Work Detail" />
          </SecondaryImage>
        </ImageComposition>

        <TextSide>
          <Subtitle
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            About me
          </Subtitle>
          <Title
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Hi Loves!
          </Title>
          <BodyText
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            Ich bin Simi, deine zertifizierte Expertin für Wimpern und
            Augenbrauen (seit 2021). Derzeit findet ihr mich im Studio „Finas“
            in München, Sendling Westpark.
          </BodyText>
          <BodyText
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            Schönheit und Ausdruck liegen mir am Herzen. Mit viel Leidenschaft
            und höchster Präzision kümmere ich mich um deine Wimpern und
            Augenbrauen, damit dein Blick strahlt!
          </BodyText>
          <Signature
            initial={{ opacity: 0, rotate: -5 }}
            whileInView={{ opacity: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            Simi
          </Signature>
          <Link to={"about-me"}>
            <MoreButton
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              More about me
            </MoreButton>
          </Link>
        </TextSide>
      </Wrapper>
    </AboutSection>
  );
};

export default AboutMe;
